import './leaflet-sidebar.css'
import { FlightViewMapLibMap } from '../map'
import { ATMProps, InitProps, Substitutions } from '../types'
import { getFlights } from './api'
import { createElement, getAirports, sleep, getAirlineName, getAirportData, calculateIconSize } from '../utils'
import L from 'leaflet'
import './leaflet-sidebar'
import { ATLFlightInfo } from './types'
import * as tooltipLayout from './leaflet-tooltip-layout'
import './atm.css'
import { FlightViewMapLibATMLegend } from './legend'
import { differenceInSeconds, format, parseISO } from 'date-fns'
import { t } from 'i18next'
import { getLocale } from '../tracker/utils'
import { FlightViewMapLibCities } from '../cities'

const tooltipCustomFields = ['airlineName', 'airlineCode', 'flightNumber', 'departureAirportCode', 'arrivalAirportCode', 'departureTime', 'arrivalTime']

const defaultTooltip = `
<img alt="$airlineName$ Logo" src="https://tracker.flightview.com/images/AirlineLogo/DHTML_ATL/100x80/$airlineCode$.png" width="25" height="20">
<div class="FVLib-atm-flight-tooltip-info"><span>$airlineCode$ $flightNumber$</span>
</div>
`
const DEFAULT_INTERVAL_TIME = 60 * 1000
const EARLY_RETRY_TIME = 5 * 1000

export class FlightViewMapLibATM {
  props: ATMProps

  // submodule
  mapModule: FlightViewMapLibMap
  legend: FlightViewMapLibATMLegend
  cities: FlightViewMapLibCities

  // map
  map: L.Map

  planeIconSize: number
  planeIconArrivalUrl: string = 'https://tracker.flightview.com/images/svg/ATLplaneRed04.svg'
  planeIconDepartureUrl: string = 'https://tracker.flightview.com/images/svg/ATLplaneBlu04.svg'

  departurePlaneIcon: L.Icon
  arrivalPlaneIcon: L.Icon

  departureFlightsLayer: L.LayerGroup
  arrivalFlightsLayer: L.LayerGroup
  departureFlights: ATLFlightInfo[] = []
  arrivalFlights: ATLFlightInfo[] = []
  mapStyle: string
  flightInfo: HTMLElement
  selectedFlight: ATLFlightInfo
  selectedFlightDirection: string
  flightInfoControl: any
  speedUnit: 'M' | 'K' = 'M'
  displayDepartures: boolean = true
  displayArrivals: boolean = true
  interactive: boolean = true
  timeFormat: string
  dateFormat: string
  locale: string
  mainAirport: string
  initialBounds: L.LatLngBounds
  zoomIn: boolean = true
  zoomDuration: number = 3.5 // seconds
  zoomAnimate: boolean = true
  zoomPause: number = 18000 // milliseconds
  substitutions: Substitutions

  constructor (parentElement: HTMLElement, initProps: InitProps) {
    this.props = initProps.atm
    this.mapModule = new FlightViewMapLibMap(parentElement, initProps)
    this.map = this.mapModule.map
    this.planeIconSize = calculateIconSize(this.props.planeIconSize, this.planeIconSize)
    this.planeIconDepartureUrl = this.props.planeIconDepartureUrl || this.planeIconDepartureUrl
    this.planeIconArrivalUrl = this.props.planeIconArrivalUrl || this.planeIconArrivalUrl
    this.mapStyle = initProps.mapStyle || 'streets'
    this.displayDepartures = this.props.displayDepartures !== undefined ? this.props.displayDepartures : true
    this.displayArrivals = this.props.displayArrivals !== undefined ? this.props.displayArrivals : true
    this.interactive = initProps.interactive !== undefined ? initProps.interactive : true
    this.timeFormat = initProps.timeFormat
    this.dateFormat = initProps.dateFormat
    this.locale = initProps.locale || 'en'
    this.substitutions = initProps.substitutions || null

    this.departurePlaneIcon = this.createPlaneIcon(this.planeIconDepartureUrl)
    this.arrivalPlaneIcon = this.createPlaneIcon(this.planeIconArrivalUrl)
    this.departureFlightsLayer = new L.LayerGroup().addTo(this.map)
    this.arrivalFlightsLayer = new L.LayerGroup().addTo(this.map)
    this.cities = new FlightViewMapLibCities(this.map, this.props)

    if (typeof this.props.airport === 'string') {
      this.mainAirport = this.props.airport
    } else {
      this.mainAirport = this.props.airport.code
    }
    this.updateFlights()
    this.flightInfo = this.mapModule.mapContainer.appendChild(createElement('sidebar'))
    this.flightInfoControl = L.control.sidebar('sidebar', {
      position: 'left'
    })
    this.map.addControl(this.flightInfoControl)
    void this.cities.initialize().then(async () => {
      await this.onMapResize()
      this.mapModule.ready(true)
      this.legend = new FlightViewMapLibATMLegend(this, this.props.airlines?.include, this.departureFlightsLayer, this.arrivalFlightsLayer, this.mapStyle, this.mapModule, this.interactive, this.timeFormat, this.dateFormat, this.locale, this.substitutions)
      if (this.props.customerLogo) {
        const logoPosition = this.props.customerLogo.position || 'topright'
        const CustomerLogoControl = L.Control.extend({
          options: {
            position: logoPosition
          },
          onAdd: () => {
            const div = L.DomUtil.create('div', 'FVLib-atm-customer-logo-container')
            const img = L.DomUtil.create('img', 'FVLib-atm-customer-logo')
            img.src = this.props.customerLogo.src
            div.appendChild(img)
            return div
          }
        })
        this.map.addControl(new CustomerLogoControl())
      }

      if (this.props.zoomFeature) {
        this.initialBounds = this.map.getBounds()
        this.zoomPause = this.props.zoomFeature.pause || this.zoomPause
        this.zoomDuration = this.props.zoomFeature.duration || this.zoomDuration
        this.zoomAnimate = this.props.zoomFeature.animate !== undefined ? this.props.zoomFeature.animate : true
        this.map.on('zoomstart', this.onZoomStart, this)
        this.map.on('zoomend', this.onZoomEnd, this)
        setTimeout(() => this.zoomStep(0), this.zoomPause)
      }

      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.map.on('resize', this.onMapResize, this)
    }).catch((err) => {
      console.error('Cannot retrieve data', err)
    })

    if (!this.interactive) {
      this.map.removeEventListener('dblclick')
    }
  }

  scheduleFlightsUpdate (time: number) {
    void sleep(time).then(() => {
      this.updateFlights()
    })
  }

  updateFlights () {
    let promises
    // if interactive, potentially the end user will want to display all directions if it's not the case yet, so fetch both directions
    // if not interactive, then we will never need to display the other direction if not displayed yet, so don't waste bandwith to fetch it
    if (this.interactive) {
      promises = [getFlights(this.mainAirport, 'Arrivals', this.props.includeEstimates), getFlights(this.mainAirport, 'Departures', this.props.includeEstimates)]
    } else {
      promises = []
      if (this.displayArrivals) {
        promises.push(getFlights(this.mainAirport, 'Arrivals', this.props.includeEstimates))
      }
      if (this.displayDepartures) {
        promises.push(getFlights(this.mainAirport, 'Departures', this.props.includeEstimates))
      }
    }
    let retryEarly = false
    void Promise.allSettled(promises).then((results) => {
      const arrivalsResult = promises.length === 2 || this.displayArrivals ? results[0] : null
      const departuresResult = promises.length === 2 ? results[1] : this.displayDepartures ? results[0] : null
      if (arrivalsResult) {
        if (arrivalsResult.status === 'fulfilled') {
          this.arrivalFlights = arrivalsResult.value
        } else {
          retryEarly = true
        }
      }
      if (departuresResult) {
        if (departuresResult.status === 'fulfilled') {
          this.departureFlights = departuresResult.value
        } else {
          retryEarly = true
        }
      }
      const shouldUpdateLastUpdated =
        (this.displayArrivals ? arrivalsResult?.status === 'fulfilled' : true) &&
        (this.displayDepartures ? departuresResult?.status === 'fulfilled' : true)

      if (shouldUpdateLastUpdated) {
        this.mapModule.updateLastUpdated()
      }

      if (this.flightInfoControl.isVisible() && this.selectedFlight) {
        const allFlights = this.arrivalFlights.concat(this.departureFlights)
        const findMatch = allFlights.find((flight) => flight.id === this.selectedFlight.id)
        if (findMatch) {
          void this.updatePlane(findMatch)
          this.createFlightDetail(findMatch)
        }
      }
      void this.drawAllPlanes()
      this.scheduleFlightsUpdate(retryEarly ? EARLY_RETRY_TIME : DEFAULT_INTERVAL_TIME)
    })
  }

  async drawAllPlanes (airlines?: string[]) {
    if (!this.flightInfoControl.isVisible()) {
      tooltipLayout.removeMarkers()
      this.departureFlightsLayer.clearLayers()
      this.arrivalFlightsLayer.clearLayers()

      if (this.displayDepartures) {
        await this.addPlanesOnMap(this.departureFlights, airlines, this.departurePlaneIcon, 'Departures')
      }
      if (this.displayArrivals) {
        await this.addPlanesOnMap(this.arrivalFlights, airlines, this.arrivalPlaneIcon, 'Arrivals')
      }

      tooltipLayout.initialize(this.map, this.onPolylineCreated)
    }
  }

  onPolylineCreated (ply: L.Polyline) {
    ply.setStyle({
      weight: 1,
      className: 'fvlib-atm-polyline'
    })
  }

  createPlaneIcon (url: string): L.Icon {
    return L.icon({
      iconUrl: url,
      iconSize: [this.planeIconSize, this.planeIconSize],
      iconAnchor: [this.planeIconSize / 2, this.planeIconSize / 2]
    })
  }

  async addPlanesOnMap (flights: ATLFlightInfo[], checked: string[], icon: L.Icon, direction: 'Departures' | 'Arrivals') {
    const layer = direction === 'Departures' ? this.departureFlightsLayer : this.arrivalFlightsLayer
    const mainAirport = await getAirportData(this.mainAirport)
    for (const flight of flights) {
      if (!flight.latitude || !flight.longitude) {
        continue
      } else if (checked && !checked.includes(flight.airlineCode)) {
        continue
      } else if (this.props.airlines && (this.props.airlines.include || this.props.airlines.exclude)) {
        const airlineCode = flight.airlineCode
        if (this.props.airlines.include && !this.props.airlines.include.includes(airlineCode)) {
          continue
        }
        if (this.props.airlines.exclude?.includes(airlineCode)) {
          continue
        }
      }
      flight.longitude += flight.longitude - mainAirport.lng > 180 ? -360 : mainAirport.lng - flight.longitude > 180 ? 360 : 0
      const marker = L.marker([flight.latitude, flight.longitude], {
        zIndexOffset: 500,
        icon,
        rotationAngle: flight.orientation * 180 / Math.PI
      }).addTo(layer)

      marker.bindTooltip(await this.createToolTipContent(flight), {
        offset: [10, 0],
        permanent: true,
        className: 'FVLib-atm-flight-tooltip'
      })
      tooltipLayout.resetMarker(marker)
      if (this.interactive) {
        marker.addEventListener('click', () => {
          this.createFlightDetail(flight)
          if (!this.flightInfoControl.isVisible()) {
            this.flightInfoControl.show()
            this.selectedFlightDirection = direction
            tooltipLayout.showOnlyCurrentMarker(marker, this.map)
            marker.unbindTooltip()
          }
          this.legend.onClose(document.getElementById('FVLib-atm-legend-content'))
        })
      }
      marker.removeEventListener('mousemove')
    }
  }

  createFlightDetail (flight: ATLFlightInfo) {
    while (this.flightInfo.firstChild) {
      this.flightInfo.removeChild(this.flightInfo.firstChild)
    }
    this.selectedFlight = flight
    const airlineDiv = L.DomUtil.create('div', 'FVLib-atm-airline-info', this.flightInfo)
    const airlineCode = L.DomUtil.create('span', 'FVLib-atm-airline-code', airlineDiv)
    airlineCode.innerText = `${flight.airlineCode} ${flight.flightNumber}`
    const airlineFullName = L.DomUtil.create('span', 'FVLib-atm-airline-code-label', airlineDiv)
    getAirlineName(flight.airlineCode).then((airLineName) => {
      airlineFullName.innerText = airLineName
    }).catch((err) => {
      console.error('Cannot get airline name', err)
    })

    const airportDiv = L.DomUtil.create('div', 'FVLib-atm-airport-info', this.flightInfo)
    const airportGroup = L.DomUtil.create('div', 'FVLib-atm-airport-group', airportDiv)

    const airportDeparture = L.DomUtil.create('div', 'FVLib-atm-airport-item', airportGroup)
    this.createAirportInfo(airportDeparture, flight, 'Departure')

    const airportArrival = L.DomUtil.create('div', 'FVLib-atm-airport-item', airportGroup)
    this.createAirportInfo(airportArrival, flight, 'Arrival')

    const progress = this.calculateProgress(flight)

    if (progress) {
      const progressBar = L.DomUtil.create('div', 'FVLib-atm-airline-info animated-progress progress-yellow', this.flightInfo)
      const progressBarIcon = L.DomUtil.create('a', '', progressBar)
      progressBarIcon.style.width = `${progress}%`
    }

    const airportMeasureValue = L.DomUtil.create('div', 'FVLib-atm-airport-measure', this.flightInfo)

    if (flight.speed) {
      const speedValue = L.DomUtil.create('span', '', airportMeasureValue)
      const formatted = Math.round(this.speedUnit === 'M' ? flight.speed * 1.15077945 : flight.speed * 1.852)
      const unit = this.speedUnit === 'M' ? 'mph' : 'kmph'
      speedValue.innerText = `${t('ATM.DETAILS_SIDEBAR.SPEED', {
        value: formatted,
        unit
      })}`
    }

    if (flight.altitude) {
      const altitudeValue = L.DomUtil.create('span', '', airportMeasureValue)
      const normalValue = Intl.NumberFormat().format(flight.altitude)
      altitudeValue.innerText = `${t('ATM.DETAILS_SIDEBAR.ALTITUDE', { value: normalValue })}`
    }
    this.flightInfoControl.getCloseButton().addEventListener('click', () => {
      void this.drawAllPlanes()
    })
  }

  calculateProgress (flight: ATLFlightInfo): number {
    if (!flight.remainingTime) {
      return null
    }
    const start = parseISO(`${flight.departureDateUtc}T${flight.departureTimeUtc}`)
    const end = parseISO(`${flight.arrivalDateUtc}T${flight.arrivalTimeUtc}`)
    const parsedRemaining = flight.remainingTime.split(':')
    if (parsedRemaining.length !== 3) {
      return null
    }
    const flightDurationInSeconds = differenceInSeconds(end, start)
    const remainingInSeconds = (+parsedRemaining[0]) * 60 * 60 + (+parsedRemaining[1]) * 60 + (+parsedRemaining[2])
    const remainingInPct = Math.round((remainingInSeconds / flightDurationInSeconds) * 100)
    return 100 - remainingInPct
  }

  createAirportInfo (container: HTMLElement, flight: ATLFlightInfo, type: string) {
    const airportCodeInfo = L.DomUtil.create('div', 'FVLib-atm-airport-code-div', container)
    const airportCode = L.DomUtil.create('span', 'FVLib-atm-airport-code', airportCodeInfo)
    airportCode.innerText = type === 'Departure' ? flight.departureAirportCode : flight.arrivalAirportCode
    const airportCodeLabel = L.DomUtil.create('span', 'FVLib-atm-airport-code-label', airportCodeInfo)
    const customCode = type === 'Departure' ? flight.departureAirportCode : flight.arrivalAirportCode
    this.getAirportLabel(customCode, airportCodeLabel)

    const airportTimeGroup = L.DomUtil.create('div', 'FVLib-atm-airport-time-group', container)
    const actualTimeDiv = L.DomUtil.create('div', 'FVLib-atm-airport-time-item', airportTimeGroup)
    const actualTimeLabel = L.DomUtil.create('span', '', actualTimeDiv)
    actualTimeLabel.innerText = t('ATM.DETAILS_SIDEBAR.ACTUAL')
    const actualTime = L.DomUtil.create('span', 'FVLib-atm-airport-time-value', actualTimeDiv)
    actualTime.innerText = this.filterTime(type === 'Departure' ? flight.departureTime : flight.arrivalTime)
  }

  getAirportLabel (code: string, element: HTMLElement) {
    const airportPromise = getAirports()
    Promise.allSettled([airportPromise]).then(async () => {
      const airports = await airportPromise
      const customerAirport = airports.find(airport => airport.code === code)
      element.innerText = customerAirport.label
    })
  }

  filterTime (time: string): string {
    const timeArr = time.split(':')
    return timeArr[0] + '.' + timeArr[1]
  }

  async updatePlane (flight: ATLFlightInfo) {
    if ((flight.altitude !== this.selectedFlight.altitude) || (flight.longitude !== this.selectedFlight.longitude)) {
      tooltipLayout.removeMarkers()
      const layer = this.selectedFlightDirection === 'Departures' ? this.departureFlightsLayer : this.arrivalFlightsLayer
      layer.clearLayers()
      const marker = L.marker([flight.latitude, flight.longitude], {
        zIndexOffset: 500,
        icon: this.selectedFlightDirection === 'Departures' ? this.departurePlaneIcon : this.arrivalPlaneIcon,
        rotationAngle: flight.orientation * 180 / Math.PI
      }).addTo(layer)

      tooltipLayout.resetMarker(marker)
      layer.addTo(this.map)
    }
  }

  async createToolTipContent (flight: ATLFlightInfo): Promise<HTMLElement> {
    const div = document.createElement('div')
    div.className = 'FVLib-atm-flight-tooltip-inner'
    let tooltipContent = this.props.flightTooltip || defaultTooltip
    for (const field of tooltipCustomFields) {
      if (field === 'airlineName' && tooltipContent.includes('$airlineName$')) {
        // load airline names only if necessary
        const airlineName = await getAirlineName(flight.airlineCode)
        tooltipContent = tooltipContent.replaceAll('$airlineName$', airlineName)
      }
      if (field === 'departureTime' && tooltipContent.includes('$departureTime$')) {
        const timeVal = format(
          new Date(flight.departureDate + 'T' + flight.departureTime), this.timeFormat || this.dateFormat || 'HH:mm',
          { locale: getLocale(this.locale) }
        )
        tooltipContent = tooltipContent.replaceAll('$departureTime$', `${timeVal}`)
      }
      if (field === 'arrivalTime' && tooltipContent.includes('$arrivalTime$')) {
        const timeVal = format(
          new Date(flight.arrivalDate + 'T' + flight.arrivalTime), this.timeFormat || this.dateFormat || 'HH:mm',
          { locale: getLocale(this.locale) }
        )
        tooltipContent = tooltipContent.replaceAll('$arrivalTime$', `${timeVal}`)
      }
      Object.entries(flight).find(([key, value]) => {
        if (field === key) {
          tooltipContent = tooltipContent.replaceAll('$' + field + '$', value)
          return div
        }
        return false
      })
    }
    div.innerHTML = tooltipContent
    return div
  }

  async onMapResize () {
    if (this.props.centerOnAirport && this.props.zoom) {
      const airports = await getAirports()
      const match = airports.find((a) => a.code === this.mainAirport)
      if (match) {
        this.map.setView([match.lat, match.lng], this.props.zoom)
        return
      }
    }
    if (this.props.center && this.props.center.length === 2 && this.props.zoom) {
      this.map.setView(this.props.center, this.props.zoom)
      return
    }
    if (this.props.bounds && this.props.bounds.length === 2) {
      const bounds = new L.LatLngBounds({
        lat: this.props.bounds[0][0],
        lng: this.props.bounds[0][1]
      }, { lat: this.props.bounds[1][0], lng: this.props.bounds[1][1] })
      L.bounds(this.props.bounds)
      this.map.fitBounds(bounds, { animate: false, padding: [20, 20] })
    } else if (this.cities) {
      const bounds = this.cities.getBounds()
      this.map.fitBounds(bounds, { animate: false, padding: [20, 20] })
    }
  }

  onZoomStart () {
    this.map.removeLayer(this.departureFlightsLayer)
    this.map.removeLayer(this.arrivalFlightsLayer)
    this.cities.removeLayer()
  }

  onZoomEnd () {
    this.map.addLayer(this.departureFlightsLayer)
    this.map.addLayer(this.arrivalFlightsLayer)
    this.cities.addLayer()
    // we must force a redraw in non-animated mode or tooltips and lines are not properly
    if (!this.zoomAnimate) {
      void sleep(100).then(() => {
        tooltipLayout.redraw(this.map)
      })
    }
  }

  zoomStep (index: number) {
    if (this.zoomIn) {
      const step = this.props.zoomFeature.steps[index]
      this.map.flyTo([step.center.lat, step.center.lng], step.zoom, { animate: this.zoomAnimate, duration: this.zoomDuration })
      this.zoomIn = false
      setTimeout(() => {
        this.zoomStep(index + 1)
      }, this.zoomPause)
    } else {
      this.map.flyToBounds(this.initialBounds, { animate: this.zoomAnimate, duration: this.zoomDuration })
      this.zoomIn = true
      setTimeout(() => {
        this.zoomStep(index === this.props.zoomFeature.steps.length ? 0 : index)
      }
      , this.zoomPause)
    }
  }
}
